import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CalendarClock, Mail, X, Trash2, Send, CheckCircle2, AlertTriangle,
  Loader2, Clock, RefreshCw,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  SCHEDULE_FREQUENCIES, SCHEDULE_FORMATS, SCHEDULE_TIMEZONES,
} from '../../../analytics-shared/constants';
import { reportsApi, schedulerStatusApi, scheduleAttachmentsApi } from '../../api/reportsApi';
import { principalsApi } from '../../../analytics-shared/api/sharesApi';

/**
 * ScheduleReportModal — the user's primary control surface for the
 * end-to-end scheduling + delivery pipeline.
 *
 * Wired through the dedicated wedge-4 endpoints so the save call:
 *   1. Validates frequency / recipients server-side.
 *   2. Computes `next_run` immediately (no 60s backfill wait).
 *   3. Returns the canonical schedule blob (with server timestamps).
 *
 * UX features added in this fix pass:
 *   - "Send test now" button that fires a delivery immediately, so
 *     users can prove the full pipeline (engine run → export render
 *     → SMTP → recipient inbox) before relying on the schedule.
 *   - Misconfig banner driven by /v1/scheduler/status — the most
 *     common cause of "schedule saved but no email" is the SES sender
 *     defaulting to the unverified `noreply@crm.example.com`. The
 *     banner surfaces that immediately with the env vars to set.
 *   - Recent delivery history panel — last 5 runs with status badge
 *     so users can see "yes the 09:00 firing went out to all 3
 *     recipients" without asking an admin.
 */
const ScheduleReportModal = ({ open, onClose, schedule, reportId, onSaved }) => {
  const [form, setForm] = useState({
    frequency: 'daily', time: '09:00', timezone: 'Asia/Kolkata', format: 'CSV', recipients: [],
  });
  const [emailInput, setEmailInput] = useState('');
  // Tenant-user autocomplete. `userMatches` is populated by a debounced
  // hit to GET /api/users?search=… (via principalsApi.searchUsers, the
  // same endpoint the wedge-3 ShareModal uses, so we don't double the
  // surface area). Users can still type a raw email and hit Enter —
  // both paths converge on addRecipient().
  const [userMatches, setUserMatches] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  // Tenant groups — recipients can be whole groups ("group:<id>" tokens,
  // expanded to member emails at send time so membership stays live).
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!open) return;
    principalsApi.listGroups()
      .then((rows) => setGroups(rows || []))
      .catch(() => setGroups([]));
  }, [open]);

  const groupLabel = useCallback((token) => {
    const id = token.slice('group:'.length);
    const g = groups.find((x) => x.id === id);
    return g ? g.label : 'Group';
  }, [groups]);

  const groupMatches = useMemo(() => {
    const q = emailInput.trim().toLowerCase();
    if (q.length < 2) return [];
    const taken = new Set(form.recipients);
    return groups
      .filter((g) => (g.label || '').toLowerCase().includes(q) && !taken.has(`group:${g.id}`))
      .slice(0, 5);
  }, [emailInput, groups, form.recipients]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);

  // Reset form to the current schedule each time the modal opens.
  // Salesforce-style extensions:
  //   weekdays    [0..6] Mon..Sun, used only for weekly frequency.
  //   monthDay    1..31 day-of-month, used only for monthly frequency.
  //   conditions  multi-rule gate (AND); legacy single-rule `condition`
  //               still rendered if present so previously-saved
  //               schedules keep working unchanged.
  //   additional_attachments  list of GridFS ids — file metadata is
  //               cached in `attachmentMeta` (below) so the row can
  //               render name/size without an extra fetch per id.
  useEffect(() => {
    if (!open) return;
    setForm(schedule
      ? {
        frequency: schedule.frequency || 'daily',
        time: schedule.time || '09:00',
        timezone: schedule.timezone || 'Asia/Kolkata',
        format: (schedule.format || 'CSV').toUpperCase(),
        recipients: Array.isArray(schedule.recipients) ? schedule.recipients : [],
        weekdays: Array.isArray(schedule.weekdays) ? schedule.weekdays : [],
        monthDay: schedule.monthDay ?? null,
        condition: schedule.condition || { type: 'always', threshold: null },
        conditions: Array.isArray(schedule.conditions) ? schedule.conditions : [],
        additional_attachments: Array.isArray(schedule.additional_attachments)
          ? schedule.additional_attachments : [],
      }
      : {
        frequency: 'daily', time: '09:00', timezone: 'Asia/Kolkata',
        format: 'CSV', recipients: [],
        weekdays: [], monthDay: null,
        condition: { type: 'always', threshold: null },
        conditions: [],
        additional_attachments: [],
      });
  }, [open, schedule]);

  // Attachment metadata cache — populated by listing the tenant's
  // GridFS uploads once on open. Keyed by id so the chips can render
  // name + size even after a hard refresh.
  const [attachmentMeta, setAttachmentMeta] = useState({});
  const [attachmentBusy, setAttachmentBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    scheduleAttachmentsApi.list()
      .then((rows) => {
        const map = {};
        (rows || []).forEach((r) => { map[r.id] = r; });
        setAttachmentMeta(map);
      })
      .catch(() => setAttachmentMeta({}));
  }, [open]);

  // Pull scheduler status (liveness + sender config) when the modal
  // mounts so the user sees red/green dots before they bother saving.
  // Best-effort: a 404/403 here just hides the banner, never blocks.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    schedulerStatusApi.get()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  // Delivery history — refreshed on open + after Send Test Now so the
  // user can immediately see whether their test actually went out.
  const refreshHistory = useCallback(async () => {
    if (!reportId) return;
    try {
      const rows = await reportsApi.listDeliveries(reportId, 5);
      setHistory(Array.isArray(rows) ? rows : []);
    } catch {
      setHistory([]);
    }
  }, [reportId]);

  useEffect(() => {
    if (!open) return;
    refreshHistory();
  }, [open, refreshHistory]);

  // Tenant-user autocomplete (debounced). Hits /api/users?search=<q>.
  // Skips queries shorter than 2 chars so we don't slam the backend
  // while the user is still typing a single character. Filters out
  // users whose email is already in the recipient pill list.
  useEffect(() => {
    if (!open) return undefined;
    const q = emailInput.trim();
    if (q.length < 2) {
      setUserMatches([]);
      setUserSearching(false);
      return undefined;
    }
    setUserSearching(true);
    const t = setTimeout(() => {
      principalsApi.searchUsers(q, 8)
        .then((rows) => {
          const taken = new Set(form.recipients.map((r) => r.toLowerCase()));
          setUserMatches(
            (rows || []).filter((r) => r.sublabel && !taken.has(r.sublabel.toLowerCase())),
          );
        })
        .catch(() => setUserMatches([]))
        .finally(() => setUserSearching(false));
    }, 250);
    return () => { clearTimeout(t); setUserSearching(false); };
  }, [emailInput, open, form.recipients]);

  const addEmail = () => {
    const e = emailInput.trim();
    if (e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && !form.recipients.includes(e)) {
      setForm((f) => ({ ...f, recipients: [...f.recipients, e] }));
      setEmailInput('');
      setUserMatches([]);
    }
  };

  const addRecipient = (email) => {
    const clean = (email || '').trim();
    if (!clean) return;
    if (form.recipients.includes(clean)) return;
    setForm((f) => ({ ...f, recipients: [...f.recipients, clean] }));
    setEmailInput('');
    setUserMatches([]);
  };

  const handleSave = useCallback(async () => {
    if (!reportId) {
      toast.error('Save the report before scheduling.');
      return;
    }
    if (!form.recipients.length) {
      toast.error('Add at least one recipient.');
      return;
    }
    // Normalise the payload — omit fields the backend treats as
    // defaults so the saved schedule blob stays tidy:
    //  - condition: drop when "always" (or use conditions[] instead)
    //  - conditions: drop when empty
    //  - weekdays: drop unless weekly
    //  - monthDay: drop unless monthly
    const payload = { ...form };
    const cond = form.condition;
    if (!cond || cond.type === 'always' || (form.conditions && form.conditions.length > 0)) {
      delete payload.condition;
    } else {
      payload.condition = {
        type: cond.type,
        threshold: cond.threshold == null ? null : Number(cond.threshold),
      };
    }
    if (!form.conditions || form.conditions.length === 0) {
      delete payload.conditions;
    } else {
      payload.conditions = form.conditions
        .filter((c) => c && c.aggregate && c.operator && c.value != null && c.value !== '')
        .map((c) => ({
          aggregate: c.aggregate,
          field: c.field || null,
          operator: c.operator,
          value: Number(c.value),
        }));
      if (!payload.conditions.length) delete payload.conditions;
    }
    if (form.frequency !== 'weekly' || !form.weekdays?.length) delete payload.weekdays;
    if (form.frequency !== 'monthly' || form.monthDay == null) delete payload.monthDay;
    if (!form.additional_attachments?.length) delete payload.additional_attachments;
    setSaving(true);
    try {
      // PUT /v1/reports/:id/schedule — server validates + computes
      // next_run immediately. Returns the canonical schedule blob.
      const result = await reportsApi.saveSchedule(reportId, payload);
      const nextRunIso = result?.schedule?.next_run;
      toast.success(
        nextRunIso
          ? `Schedule saved — next run ${new Date(nextRunIso).toLocaleString()}`
          : 'Schedule saved',
      );
      if (onSaved) onSaved(result?.schedule || form);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save schedule';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [form, reportId, onSaved, onClose]);

  const handleRemove = useCallback(async () => {
    if (!reportId) return;
    setSaving(true);
    try {
      await reportsApi.removeSchedule(reportId);
      toast.success('Schedule removed');
      if (onSaved) onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Failed to remove schedule');
    } finally {
      setSaving(false);
    }
  }, [reportId, onSaved, onClose]);

  const handleSendTest = useCallback(async () => {
    if (!reportId) {
      toast.error('Save the report before testing.');
      return;
    }
    const recipients = form.recipients;
    if (!recipients.length) {
      toast.error('Add at least one recipient to test.');
      return;
    }
    setTesting(true);
    try {
      // POST /v1/reports/:id/send-now — queues an immediate delivery
      // through the same pipeline the scheduler uses. Response is
      // synchronous "queued" — the actual delivery runs in a
      // BackgroundTasks task on the server. Refresh history after a
      // short wait so the user sees the new row land.
      await reportsApi.sendNow(reportId, recipients);
      toast.success('Test delivery queued — refreshing history…');
      // Render takes ~1-3s server-side (engine + export + SMTP). Poll
      // history twice so the new row shows up without an extra click.
      setTimeout(refreshHistory, 1500);
      setTimeout(refreshHistory, 4000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Send test failed';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  }, [reportId, form.recipients, refreshHistory]);

  // Status indicators. `running` is the scheduler's asyncio liveness;
  // `sender_misconfigured` is the single most common reason deliveries
  // silently fail (SES rejects unverified senders).
  const schedulerRunning = status?.scheduler?.running;
  const senderBad = status?.email?.sender_misconfigured;
  const senderHint = status?.email?.sender_misconfigured_hint;
  const senderAddress = status?.email?.sender;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* DialogContent now flex-column with a viewport-capped height so
          the modal never grows taller than the screen. Header + footer
          stay pinned; the middle (banner + form + history) lives in
          its own scrollable region. `h-[90vh]` is a fixed cap rather
          than max-h so short viewports get the same layout. */}
      <DialogContent className="max-w-2xl w-[95vw] sm:w-full p-0 gap-0 flex flex-col max-h-[92vh] sm:max-h-[88vh]">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <CalendarClock className="h-5 w-5 text-indigo-600" />
            Schedule Report
            {status && (
              <span className={`ml-2 text-[11px] font-normal flex items-center gap-1 ${
                schedulerRunning ? 'text-emerald-600' : 'text-slate-400'
              }`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                  schedulerRunning ? 'bg-emerald-500' : 'bg-slate-300'
                }`} />
                {schedulerRunning ? 'Scheduler live' : 'Scheduler offline'}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Automatically run and deliver this report on a recurring basis.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable middle region. Header (above) + Footer (below)
            stay pinned; everything between scrolls when the form is
            taller than the viewport allows. Mobile-friendly because
            the viewport cap is the only height constraint. */}
        <div className="flex-1 overflow-y-auto">

        {senderBad && (
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 flex-1">
              <div className="font-semibold mb-0.5">Email sender not verified</div>
              <div>Current sender: <code className="bg-amber-100 px-1 rounded">{senderAddress}</code></div>
              {senderHint && <div className="mt-1 text-amber-700">{senderHint}</div>}
            </div>
          </div>
        )}

        <div className="px-6 py-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Frequency</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {SCHEDULE_FREQUENCIES.map((fr) => (
                <button key={fr} type="button" onClick={() => setForm((f) => ({ ...f, frequency: fr }))}
                  className={`py-2 rounded-lg text-sm capitalize border transition-colors ${
                    form.frequency === fr
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}>
                  {fr}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Time</label>
              <Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Timezone</label>
              <select value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                className="w-full mt-1 h-10 border border-slate-200 rounded-md px-2 text-sm bg-white">
                {SCHEDULE_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>

          {/* ── Weekly day-of-week selector ─────────────────────────────
              Only rendered for weekly frequency. The backend's
              compute_next_run walks at most 7 days forward looking for
              the first weekday in this set whose HH:MM is after now.
              No selection = legacy "every 7 days from now" behaviour. */}
          {form.frequency === 'weekly' && (
            <div>
              <label className="text-xs font-medium text-slate-600">On these days</label>
              <div className="grid grid-cols-7 gap-1 mt-1">
                {[
                  { i: 0, label: 'Mon' }, { i: 1, label: 'Tue' }, { i: 2, label: 'Wed' },
                  { i: 3, label: 'Thu' }, { i: 4, label: 'Fri' }, { i: 5, label: 'Sat' },
                  { i: 6, label: 'Sun' },
                ].map(({ i, label }) => {
                  const checked = (form.weekdays || []).includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setForm((f) => ({
                        ...f,
                        weekdays: checked
                          ? (f.weekdays || []).filter((d) => d !== i)
                          : [...(f.weekdays || []), i].sort((a, b) => a - b),
                      }))}
                      className={`py-1.5 rounded-md text-xs border transition-colors ${
                        checked
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {!form.weekdays?.length && (
                <div className="text-[10px] text-slate-400 mt-1">
                  Pick one or more days. Empty = every 7 days from the next firing.
                </div>
              )}
            </div>
          )}

          {/* ── Monthly day-of-month selector ───────────────────────────
              Only rendered for monthly frequency. Day-of-month 1..31;
              backend clamps to month length (Feb-31 → 28/29). */}
          {form.frequency === 'monthly' && (
            <div>
              <label className="text-xs font-medium text-slate-600">Day of month</label>
              <Input
                type="number"
                min="1" max="31"
                value={form.monthDay ?? ''}
                placeholder="e.g. 15 (1–31)"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, monthDay: v === '' ? null : Number(v) }));
                }}
                className="mt-1 w-32"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Days past month length (e.g. 31 in Feb) roll back to the last valid day.
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600">Export Format</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {SCHEDULE_FORMATS.map((fmt) => (
                <button key={fmt} type="button" onClick={() => setForm((f) => ({ ...f, format: fmt }))}
                  className={`py-2 rounded-lg text-sm border transition-colors ${
                    form.format === fmt
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}>
                  {fmt}
                </button>
              ))}
            </div>
          </div>

          {/* ── Conditional delivery (Salesforce-style) ────────────────────
              Optional gate evaluated AFTER the engine produces a record
              count, BEFORE any SMTP work. "Always" is the default and
              matches pre-wedge behaviour — every firing delivers. The
              min/max variants surface a threshold input only when
              relevant. */}
          <div>
            <label className="text-xs font-medium text-slate-600">Send condition</label>
            <div className="flex gap-1.5 mt-1">
              <select
                value={form.condition?.type || 'always'}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  condition: { ...(f.condition || {}), type: e.target.value },
                }))}
                className="flex-1 h-10 border border-slate-200 rounded-md px-2 text-sm bg-white"
              >
                <option value="always">Always send</option>
                <option value="has_records">Only if report has records</option>
                <option value="min_records">Only if record count ≥ threshold</option>
                <option value="max_records">Only if record count ≤ threshold</option>
              </select>
              {(form.condition?.type === 'min_records' || form.condition?.type === 'max_records') && (
                <Input
                  type="number"
                  min="0"
                  value={form.condition?.threshold ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setForm((f) => ({
                      ...f,
                      condition: {
                        ...(f.condition || {}),
                        threshold: raw === '' ? null : Number(raw),
                      },
                    }));
                  }}
                  placeholder="Threshold"
                  className="w-28 h-10"
                />
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              Manual "Send test now" always delivers regardless of this setting.
            </div>

            {/* ── Multi-rule conditions (Salesforce style, AND logic) ──
                Rendered below the legacy single-condition dropdown so
                older saved schedules still display their gate. If the
                user adds any multi-rule conditions, the save payload
                drops the legacy `condition` field and uses
                `conditions` instead (see handleSave normaliser). */}
            <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-slate-600">
                  Advanced conditions
                  {form.conditions?.length > 0 && (
                    <span className="ml-1.5 text-[10px] text-indigo-600">
                      ALL must pass (AND)
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium"
                  onClick={() => setForm((f) => ({
                    ...f,
                    conditions: [
                      ...(f.conditions || []),
                      { aggregate: 'count', field: null, operator: 'gt', value: 0 },
                    ],
                  }))}
                >
                  + Add condition
                </button>
              </div>
              {(form.conditions || []).map((rule, idx) => (
                <div key={idx} className="mt-1.5 flex gap-1.5 items-center">
                  <select
                    value={rule.aggregate}
                    onChange={(e) => {
                      const next = [...form.conditions];
                      const newAgg = e.target.value;
                      // Switching to/from `count` clears the field — count
                      // doesn't reference one and we don't want stale state.
                      next[idx] = {
                        ...rule,
                        aggregate: newAgg,
                        field: newAgg === 'count' ? null : (rule.field || ''),
                      };
                      setForm((f) => ({ ...f, conditions: next }));
                    }}
                    className="h-9 border border-slate-200 rounded-md px-2 text-xs bg-white"
                  >
                    <option value="count">Record count</option>
                    <option value="sum">Sum</option>
                    <option value="avg">Average</option>
                    <option value="min">Min</option>
                    <option value="max">Max</option>
                  </select>
                  {rule.aggregate !== 'count' && (
                    <Input
                      value={rule.field || ''}
                      onChange={(e) => {
                        const next = [...form.conditions];
                        next[idx] = { ...rule, field: e.target.value };
                        setForm((f) => ({ ...f, conditions: next }));
                      }}
                      placeholder="field key"
                      className="h-9 w-28 text-xs"
                    />
                  )}
                  <select
                    value={rule.operator}
                    onChange={(e) => {
                      const next = [...form.conditions];
                      next[idx] = { ...rule, operator: e.target.value };
                      setForm((f) => ({ ...f, conditions: next }));
                    }}
                    className="h-9 border border-slate-200 rounded-md px-2 text-xs bg-white"
                  >
                    <option value="eq">=</option>
                    <option value="ne">≠</option>
                    <option value="gt">&gt;</option>
                    <option value="gte">≥</option>
                    <option value="lt">&lt;</option>
                    <option value="lte">≤</option>
                  </select>
                  <Input
                    type="number"
                    value={rule.value ?? ''}
                    onChange={(e) => {
                      const next = [...form.conditions];
                      const v = e.target.value;
                      next[idx] = { ...rule, value: v === '' ? '' : Number(v) };
                      setForm((f) => ({ ...f, conditions: next }));
                    }}
                    placeholder="value"
                    className="h-9 w-24 text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.conditions.filter((_, i) => i !== idx);
                      setForm((f) => ({ ...f, conditions: next }));
                    }}
                    className="p-1 text-slate-400 hover:text-rose-600"
                    title="Remove condition"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {form.conditions?.length > 0 && (
                <div className="text-[10px] text-slate-400 mt-1">
                  All conditions must pass for the email to send. The
                  legacy dropdown above is ignored when any advanced
                  conditions are set.
                </div>
              )}
            </div>
          </div>

          {/* ── Additional attachments ───────────────────────────────────
              Upload extra files pinned to this schedule. Each delivery
              includes them as MIME parts on the same email as the
              generated report export. Cap: 10MB per file, 25MB total. */}
          <div>
            <label className="text-xs font-medium text-slate-600">Additional attachments</label>
            <div className="mt-1 space-y-1.5">
              {(form.additional_attachments || []).map((aid) => {
                const meta = attachmentMeta[aid] || { name: aid, size: 0 };
                const kb = meta.size ? Math.max(1, Math.round(meta.size / 1024)) : null;
                return (
                  <div key={aid} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200">
                    <span className="text-xs text-slate-700 truncate flex-1">
                      {meta.name}{kb && <span className="text-slate-400 ml-2">{kb} KB</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({
                        ...f,
                        additional_attachments: (f.additional_attachments || []).filter((x) => x !== aid),
                      }))}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
              <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  disabled={attachmentBusy}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';  // allow re-picking the same file
                    if (file.size > 10 * 1024 * 1024) {
                      toast.error('File exceeds 10 MB per-file limit.');
                      return;
                    }
                    setAttachmentBusy(true);
                    try {
                      const meta = await scheduleAttachmentsApi.upload(file);
                      setAttachmentMeta((m) => ({ ...m, [meta.id]: meta }));
                      setForm((f) => ({
                        ...f,
                        additional_attachments: [...(f.additional_attachments || []), meta.id],
                      }));
                      toast.success(`${meta.name} attached`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
                    } finally {
                      setAttachmentBusy(false);
                    }
                  }}
                />
                {attachmentBusy
                  ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>)
                  : <>+ Add file</>}
              </label>
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              PDF / DOCX / XLSX / CSV / images / ZIP. Max 10 MB per file, 25 MB total per delivery.
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
              <Mail className="h-3 w-3" /> Recipients
            </label>
            <div className="flex gap-1.5 mt-1">
              <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                placeholder="Search users, groups, or type an email" className="h-9" />
              <Button type="button" variant="outline" size="sm" onClick={addEmail}>Add</Button>
            </div>
            {/* Tenant-user autocomplete. Only renders when the input
                has 2+ chars AND we have results — so the recipient
                pills below aren't pushed around by an empty dropdown
                on first render. Manual email entry still works exactly
                the same; this is purely additive UX. */}
            {(userMatches.length > 0 || userSearching) && emailInput.trim().length >= 2 && (
              <div className="mt-1 rounded-lg border border-slate-200 bg-white shadow-sm max-h-40 overflow-y-auto">
                {userSearching && userMatches.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-slate-400">Searching users…</div>
                )}
                {userMatches.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => addRecipient(u.sublabel)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-indigo-50"
                  >
                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      {(u.label || u.sublabel || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800 truncate">{u.label}</div>
                      <div className="text-[10px] text-slate-500 truncate">{u.sublabel}</div>
                    </div>
                  </button>
                ))}
                {groupMatches.map((g) => (
                  <button
                    key={`grp-${g.id}`}
                    type="button"
                    onClick={() => addRecipient(`group:${g.id}`)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-violet-50"
                  >
                    <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                      G
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800 truncate">{g.label}</div>
                      <div className="text-[10px] text-slate-500 truncate">Group — emails every member</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {form.recipients.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {form.recipients.map((e) => {
                  const isGroup = e.startsWith('group:');
                  return (
                    <span key={e} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                      isGroup ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {isGroup ? `Group: ${groupLabel(e)}` : e}
                      <button onClick={() => setForm((f) => ({ ...f, recipients: f.recipients.filter((x) => x !== e) }))}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ─── Recent delivery history ────────────────────────────────── */}
        {(history.length > 0 || schedule) && (
          <div className="px-6 pb-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Recent deliveries
              </div>
              <button onClick={refreshHistory}
                className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1">
                <RefreshCw className="h-2.5 w-2.5" /> Refresh
              </button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto bg-slate-50 rounded-lg p-2 border border-slate-200">
              {history.length === 0 && (
                <div className="text-[11px] text-slate-400 px-1.5 py-1">No deliveries yet.</div>
              )}
              {history.map((d) => {
                const status = d.status || 'unknown';
                const pillCls = status === 'sent' ? 'bg-emerald-100 text-emerald-700'
                  : status === 'partial' ? 'bg-amber-100 text-amber-700'
                    : status === 'failed' ? 'bg-rose-100 text-rose-700'
                      : 'bg-slate-200 text-slate-600';
                const when = d.started_at ? new Date(d.started_at).toLocaleString() : '—';
                const recipientCount = (d.recipients || []).length;
                return (
                  <div key={d.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white">
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${pillCls}`}>
                      {status}
                    </span>
                    <span className="text-[11px] text-slate-600 flex-1 truncate">
                      {when} · {recipientCount} recipient{recipientCount === 1 ? '' : 's'} · {d.format_delivered || d.format_requested}
                    </span>
                    {d.errors && d.errors[0] && (
                      <span className="text-[10px] text-rose-600 truncate max-w-[140px]" title={d.errors.join('\n')}>
                        {d.errors[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        </div>{/* /scrollable middle region */}

        {/* Footer pinned to the bottom of the modal. flex-shrink-0 +
            flex-wrap so the action row never gets squeezed off-screen
            on narrow viewports — buttons wrap onto a second line
            instead of being clipped. */}
        <DialogFooter className="sm:justify-between px-6 py-3 border-t border-slate-200 bg-slate-50 flex-shrink-0 gap-2 flex-wrap">
          {schedule ? (
            <Button variant="ghost" onClick={handleRemove} disabled={saving || testing}
              className="text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1.5" /> Remove Schedule
            </Button>
          ) : <span />}
          <div className="flex gap-2 items-center flex-wrap justify-end">
            <Button type="button" variant="outline" onClick={handleSendTest}
              disabled={saving || testing || !form.recipients.length}>
              {testing
                ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</>)
                : (<><Send className="h-4 w-4 mr-1.5" /> Send test now</>)}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving || testing}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || testing}
              className="bg-indigo-600 hover:bg-indigo-700">
              {saving
                ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>)
                : (<><CheckCircle2 className="h-4 w-4 mr-1.5" /> {schedule ? 'Update' : 'Schedule'}</>)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReportModal;
